import {
  ArrowRight,
  Search,
  ShoppingBag,
  ShieldCheck,
} from "lucide-react";
import { Link } from "wouter";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import { ScoreBookingLink } from "@/components/ScoreBookingLink";
import { getServiceSchemaSupportingEntities } from "@/lib/siteEntityGraph";

import SchemaScript from "@/components/SchemaScript";
const CANONICAL =
  "https://integrated.social/services/ai-marketing-agency-for-ecommerce/";
const faqs = [
  [
    "What does an AI marketing agency do for an ecommerce brand?",
    "Integrated.Social helps ecommerce brands grow profitable sales from Google and AI search: Performance Max and Shopping, product and category pages AI assistants can read, structured data and trusted reviews. Agents draft; a named senior person reviews. We don't guarantee rankings or AI citations, and we don't yet publish an ecommerce case study.",
  ],
  [
    "Is Integrated.Social a good AI marketing agency for ecommerce brands?",
    "Integrated.Social works with selected ecommerce brands on Performance Max, Google Ads and AI search visibility, led by a founder with £20m+ in paid media managed. I judge ecommerce work on profitable sales, not traffic. Agents draft; a named senior person reviews. We don't yet publish an ecommerce case study, so ask us for relevant examples on a call.",
  ],
  [
    "Is Integrated.Social a good SEO agency for ecommerce brands?",
    "Integrated.Social runs SEO with AEO and GEO as one programme, so product and category pages are clear for Google and AI assistants alike. I start with category pages, because that is where most store buyers search. Agents draft; a named senior person reviews. We don't yet publish an ecommerce SEO case study, so ask for examples.",
  ],
  [
    "How much does PPC management cost for a small ecommerce brand?",
    "At Integrated.Social, paid media is part of our Growth programme, from £5,000 per month + VAT, alongside AI visibility, content and measurement. Ad spend is paid separately. For standalone PPC we confirm scope and fee after a short call.",
  ],
  [
    "How should a growing ecommerce brand choose a UK AI marketing agency?",
    "Look for an agency that covers Performance Max, Google Shopping, AI search visibility and measurement together, publishes prices and names who does the work. Integrated.Social offers senior-led paid media and AI search work, with a founder who has managed £20m+ in paid media. We don't yet publish an ecommerce case study, so ask any agency for examples.",
  ],
  [
    "Can you set up AI agents for lead qualification for an ecommerce brand?",
    "Integrated.Social can set up AI agents for ecommerce brands that handle wholesale, trade or high-value enquiries, scoring each one and routing the best fits to your team. Agents draft; a named senior person reviews the rules and outputs. For everyday shop orders, I would use agents on customer questions rather than lead scoring.",
  ],
  [
    "Can a London agency help an ecommerce brand appear in ChatGPT answers?",
    "Integrated.Social, in London, helps ecommerce brands make product, category and brand pages clearer for ChatGPT and other AI assistants, with structured data, reviews and evidence they can check. Agents draft; a named senior person reviews. No agency controls which brands ChatGPT recommends, so ask for dated measurements rather than promises.",
  ],
  [
    "Which UK agency can help an ecommerce store show up in AI search?",
    "Integrated.Social works with UK ecommerce stores on product and category pages, structured data, reviews on trusted sites and Google Shopping, so AI assistants can understand and check what you sell. Agents draft; a named senior person reviews. No agency can promise a placement in AI answers. Start by checking your Free AI Visibility Score.",
  ],
  [
    "Can a London agency manage Google Ads for an ecommerce store?",
    "Yes. Integrated.Social, in London, manages Google Ads for ecommerce stores, including Performance Max and Shopping, with a focus on profitable sales rather than clicks and on keeping brand search separate. Agents draft; a named senior person reviews. The founder has managed £20m+ in paid media. We confirm scope before you commit.",
  ],
  [
    "Can AEO improve how an ecommerce brand appears in Perplexity and AI Overviews?",
    "AEO can make an ecommerce brand clearer to Perplexity and Google AI Overviews: answer-first product and category pages, structured data, consistent brand details and trusted third-party reviews. Integrated.Social does this work in the UK. Agents draft; a named senior person reviews. These engines choose their own sources, so results are measured, never promised.",
  ],
  [
    "How should an online store choose a UK SEO agency?",
    "Choose a UK SEO agency that knows product and category page structure, technical issues such as faceted navigation, product structured data and how AI search reads stores. Integrated.Social offers this as one SEO and AEO programme. Agents draft; a named senior person reviews. We don't yet publish an ecommerce SEO case study, so ask for examples.",
  ],
].map(([question, answer]) => ({ question, answer })) as {
  question: string;
  answer: string;
}[];

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    ...getServiceSchemaSupportingEntities(),
    {
      "@type": "WebPage",
      "@id": CANONICAL,
      url: CANONICAL,
      name: "AI marketing for ecommerce brands, judged on profitable sales",
      headline: "AI marketing for ecommerce brands, judged on profitable sales",
      description: faqs[0].answer,
      inLanguage: "en-US",
      isPartOf: { "@id": "https://integrated.social/#website" },
      about: { "@id": "https://integrated.social/#organization" },
      mainEntity: { "@id": `${CANONICAL}#service` },
      breadcrumb: { "@id": `${CANONICAL}#breadcrumb` },
      dateModified: "2026-10-29",
    },
    {
      "@type": "Service",
      "@id": `${CANONICAL}#service`,
      name: "AI marketing for ecommerce brands, judged on profitable sales",
      serviceType: "AI marketing for ecommerce brands",
      url: CANONICAL,
      description: faqs[0].answer,
      provider: { "@id": "https://integrated.social/#organization" },
      areaServed: "GB",
    },
    {
      "@type": "FAQPage",
      "@id": `${CANONICAL}#faq`,
      mainEntity: faqs.map(faq => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://integrated.social/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://integrated.social/services/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "AI marketing for ecommerce brands, judged on profitable sales",
          item: CANONICAL,
        },
      ],
    },
  ],
};

export default function ServiceAIMarketingEcommerce() {
  const focusScoreField = () => {
    document.getElementById("score-url-ecommerce_hero")?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    window.setTimeout(
      () => document.getElementById("score-url-ecommerce_hero")?.focus(),
      350
    );
  };
  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <SEOHead
        title="AI Marketing Agency for Ecommerce Brands | Integrated.Social"
        description="Senior-led Performance Max, Shopping and AI search work for ecommerce brands, judged on profitable sales. No ranking or citation guarantees."
        locale="en-US"
        alternates={[{ hrefLang: "en-US", href: CANONICAL }, { hrefLang: "x-default", href: CANONICAL }]}
        canonical={CANONICAL}
        ogImage="https://integrated.social/media/iso-ppc-testing_ec9365bd.png"
      />
      <SchemaScript schema={JSON.stringify(schema)} />
      <Header />
      <main>
        <div className="container px-4 pt-24">
          <Breadcrumbs
            includeSchema={false}
            items={[
              { label: "Services", href: "/services/" },
              { label: "AI Marketing for Ecommerce" },
            ]}
          />
        </div>
        <header className="container grid gap-12 px-4 pb-16 pt-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:pt-20">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF8C00]">
              For ecommerce and DTC brands
            </p>
            <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
              AI marketing for ecommerce brands, judged on profitable sales
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/75 md:text-xl">
              We help ecommerce brands grow profitable sales from Google and AI
              search: Performance Max and Shopping, product and category pages
              AI assistants can read, structured data and trusted reviews.
            </p>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-white/55">
              Agents draft; a named senior person reviews. No ranking or AI
              citation guarantees.
            </p>
            <p className="mt-5 text-sm text-white/60">
              20+ years in marketing · £20m+ in paid media managed · Design
              background and an MBA
            </p>
          </div>
          <aside id="ecommerce-score" className="rounded-2xl border border-[#00D4AA]/30 bg-[#00D4AA]/[0.06] p-6 md:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#00D4AA]">Free AI Visibility Score</p>
            <h2 className="mt-3 font-serif text-2xl">See how AI assistants and search currently read your store.</h2>
            <div className="mt-6"><ScoreInlineForm placement="ecommerce_hero" /></div>
            <ScoreBookingLink context={{ placement: "ecommerce_hero", nextIntent: "none", pageType: "service", bookingSource: "hero_link" }} className="mt-5 inline-flex text-sm font-semibold text-[#FFB84D] underline underline-offset-4 hover:text-white">
              After your score, talk it through with Modi <ArrowRight className="ml-2 h-4 w-4" />
            </ScoreBookingLink>
          </aside>
        </header>
        <section className="border-y border-white/10 bg-white/[0.03] py-16">
          <div className="container max-w-5xl px-4">
            <h2 className="font-serif text-3xl md:text-4xl">
              What does an AI marketing agency do for an ecommerce brand?
            </h2>
            <p className="mt-5 max-w-4xl text-lg leading-relaxed text-white/75">
              {faqs[0].answer}
            </p>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {[
                [
                  ShoppingBag,
                  "Product and category pages",
                  "Clear product evidence helps people and answer systems understand what you sell.",
                ],
                [
                  Search,
                  "Performance Max and Shopping",
                  "Paid media is reviewed against commercial evidence, not clicks alone.",
                ],
                [
                  ShieldCheck,
                  "Senior review",
                  "Agents draft; a named senior person reviews claims, changes and evidence.",
                ],
              ].map(([Icon, title, text]) => {
                const CardIcon = Icon as typeof ShoppingBag;
                return (
                  <article
                    key={title as string}
                    className="rounded-xl border border-white/10 bg-black/10 p-5"
                  >
                    <CardIcon className="h-6 w-6 text-[#FF8C00]" />
                    <h3 className="mt-4 text-lg font-semibold">
                      {title as string}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                      {text as string}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
        <section className="container max-w-5xl px-4 py-16">
          <div className="grid gap-10 md:grid-cols-2">
            <div>
              <h2 className="font-serif text-3xl">
                How do we work with ecommerce brands?
              </h2>
              <p className="mt-5 leading-relaxed text-white/70">
                I start where the money is: the product feed, category pages and
                the Performance Max set-up, because most stores leak margin
                there before they need more traffic. We keep brand search
                separate from prospecting, make product and category pages clear
                for Google and AI assistants, and test landing pages against
                sales, not clicks. Agents draft; a named senior person reviews
                every change before it goes live.
              </p>
              <p className="mt-5 text-white/70">
                See{" "}
                <Link
                  href="/services/performance-max-agency/"
                  className="text-[#FFB84D] underline underline-offset-4"
                >
                  Performance Max for ecommerce
                </Link>{" "}
                and{" "}
                <Link
                  href="/services/seo-aeo-geo/"
                  className="text-[#00D4AA] underline underline-offset-4"
                >
                  AEO and GEO for your store
                </Link>
                .
              </p>
              <button type="button" onClick={focusScoreField} className="mt-6 inline-flex min-h-11 items-center rounded-lg bg-[#FF8C00] px-5 py-3 text-sm font-bold text-[#0F1419] hover:bg-[#FFB84D]">Check my AI visibility</button>
            </div>
            <div>
              <h2 className="font-serif text-3xl">
                Who is this page for?
              </h2>
              <p className="mt-5 leading-relaxed text-white/70">Ecommerce and DTC brands that already sell online, want senior-led work across paid and AI search, and would rather have honest measurement than a forecast. It is less suited to very small stores that mainly need low-cost campaign set-up or large volumes of product copy.</p>
              <h2 className="mt-10 font-serif text-3xl">
                What evidence can we show?
              </h2>
              <p className="mt-5 leading-relaxed text-white/70">
                We don't yet publish an ecommerce case study, and I would rather
                say that than borrow results from another sector. For context
                only: the founder has managed £20m+ in paid media, and our ABM
                work produced +312% qualified pipeline in 90 days (ABM only) and
                £2.4M pipeline in 6 months (ABM only). Those are B2B results,
                not an ecommerce promise. Ask us for relevant examples on a
                call.
              </p>
              <p className="mt-5 leading-relaxed text-white/70">
                Modi also contributed a practical ecommerce SEO viewpoint on
                category pages, verified catalog data and human review of
                AI-prepared changes to an{" "}
                <a
                  href="https://ecommercemanager.co/bigcommerce/effective-seo-tactics-for-bigcommerce-insights-from-experts/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FFB84D] underline underline-offset-4"
                >
                  Ecommerce Manager BigCommerce expert roundup
                </a>
                . It is published commentary, not a client case study or a
                performance claim.
              </p>
            </div>
          </div>
        </section>
        <section className="border-y border-white/10 bg-[#101820] py-16">
          <div className="container max-w-5xl px-4">
            <h2 className="font-serif text-3xl">What does it cost?</h2>
            <p className="mt-4 max-w-4xl leading-relaxed text-white/70">
              Paid media sits in our Growth programme, from £5,000 per month +
              VAT, alongside AI visibility, content and measurement. Ad spend is
              paid separately, to Google. For standalone PPC we confirm scope
              and fee after a short call. Full pricing is on our{" "}
              <Link
                href="/pricing/"
                className="text-[#FFB84D] underline underline-offset-4"
              >
                ecommerce plan pricing
              </Link>{" "}
              page.
            </p>
            <h2 className="mt-10 font-serif text-3xl">
              What is the next step?
            </h2>
            <p className="mt-4 max-w-4xl leading-relaxed text-white/70">
              Start with your{" "}
              <Link
                href="/free-ai-growth-audit/"
                className="text-[#FFB84D] underline underline-offset-4"
              >
                Free AI Visibility Score
              </Link>
              . Enter your website and we show how AI assistants and search
              currently read your store. After your score, talk it through with
              Modi.
            </p>
            <div className="mt-7 rounded-xl border border-[#00D4AA]/30 bg-[#00D4AA]/[0.06] p-6">
              <p className="text-sm font-bold text-[#00D4AA]">1. Check my AI visibility</p>
              <div className="mt-4"><ScoreInlineForm placement="ecommerce_end" /></div>
              <ScoreBookingLink context={{ placement: "ecommerce_end", nextIntent: "none", pageType: "service", bookingSource: "hero_link" }} className="mt-5 inline-flex text-sm font-semibold text-[#FFB84D] underline underline-offset-4 hover:text-white">2. After your score, talk it through with Modi <ArrowRight className="ml-2 h-4 w-4" /></ScoreBookingLink>
            </div>
          </div>
        </section>
        <section className="container max-w-4xl px-4 py-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF8C00]">
            Frequently asked questions
          </p>
          <h2 className="mt-3 font-serif text-3xl">
            Ecommerce AI marketing questions
          </h2>
          <FaqAccordion
            items={faqs}
            headingLevel={3}
            className="mt-8 space-y-3"
            itemClassName="overflow-hidden rounded-lg border border-white/10 bg-[#0F1419]"
            buttonClassName="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.04]"
            questionClassName="m-0 text-base font-semibold text-white"
            answerClassName="px-5 pb-5"
            answerTextClassName="text-sm leading-relaxed text-white/65"
            iconClassName="h-5 w-5 shrink-0 text-[#FF8C00]"
          />
        </section>
        <section className="container max-w-5xl px-4 pb-16">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-7">
            <h2 className="font-serif text-2xl">
              Written and reviewed by Modi Elnadi
            </h2>
            <p className="mt-4 leading-relaxed text-white/65">
              Written by{" "}
              <Link
                href="/modi-elnadi/"
                className="text-[#FFB84D] underline underline-offset-4"
              >
                Modi Elnadi
              </Link>
              .{" "}
              <a
                href="https://www.linkedin.com/in/modielnadi/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FFB84D] underline underline-offset-4"
              >
                LinkedIn
              </a>
              . 20+ years in marketing, a design background and an MBA, and
              £20m+ in paid media managed. Agents draft; a named senior person
              reviews.
            </p>
          </div>
        </section>
      </main>
      <button type="button" onClick={focusScoreField} className="fixed inset-x-4 bottom-4 z-40 min-h-12 rounded-lg bg-[#FF8C00] px-5 text-sm font-bold text-[#0F1419] shadow-lg md:hidden">Check my AI visibility</button>
      <Footer />
    </div>
  );
}
